import { firebase } from "../config/configureFirebase";
import { GetDistance } from "../other/GeoFunctions";
import { onValue, child, push, update, get, query, orderByChild, equalTo } from "firebase/database";

export const isFleetAdminPrivateClientBooking = (booking) => !!(
  booking && (
    booking.bookingSource === "fleet_admin_client" ||
    booking.visibility === "private" ||
    (booking.deliveryWithBid === false && booking.booking_type_fleetadmin === true)
  )
);

export const getContainerSizeFromCarType = (carDetails) => {
  const name = typeof carDetails === "string" ? carDetails : (carDetails && carDetails.name ? carDetails.name : "");
  const normalized = name.toLowerCase();
  if (/(^|[^0-9])10\s*(ft|footer|feet)?([^0-9]|$)/.test(normalized)) return "10ft";
  if (/(^|[^0-9])20\s*(ft|footer|feet)?([^0-9]|$)/.test(normalized)) return "20ft";
  if (/(^|[^0-9])40\s*(ft|footer|feet)?([^0-9]|$)/.test(normalized)) return "40ft";
  return "";
};

export const isDriverEligibleForBookingCar = (driverCarType, bookingCarType) => {
  const driverContainerSize = getContainerSizeFromCarType(driverCarType);
  const bookingContainerSize = getContainerSizeFromCarType(bookingCarType);
  if (bookingContainerSize) {
    return !!driverContainerSize;
  }
  const bookingCarName = typeof bookingCarType === "string" ? bookingCarType : (bookingCarType && bookingCarType.name ? bookingCarType.name : "");
  return (driverCarType || "") === (bookingCarName || "");
};

export const generateBookingReference = () => {
  const digits = Math.floor(Math.random() * 10000000).toString().padStart(7, "0");
  const letters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const suffix = [...Array(3)].map(() => letters[Math.floor(Math.random() * letters.length)]).join("");
  return `${digits}${suffix}`;
};

const parseMoneyValue = (value) => {
  const amount = parseFloat(value);
  return isNaN(amount) ? null : amount;
};

const firstPresent = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");

const pendingPaymentAmount = () => ({
  amount: null,
  source: "pendingBidAcceptance",
  isBidAmount: false,
  pending: true
});

const resolvedPaymentAmount = (value, source, isBidAmount) => {
  const amount = parseMoneyValue(value);
  return amount === null ? pendingPaymentAmount() : {
    amount,
    source,
    isBidAmount,
    pending: false
  };
};

export const getFinalPaymentAmount = (booking) => {
  if (!booking) return pendingPaymentAmount();
  if (booking.acceptedOfferType === "fleet") {
    const offer = booking.acceptedFleetOffer || {};
    return resolvedPaymentAmount(firstPresent(offer.amount, offer.bidAmount), "acceptedFleetOffer", true);
  }
  if (booking.acceptedOfferType === "driver") {
    const offer = booking.acceptedDriverOffer || {};
    const selectedBid = booking.selectedBid || {};
    return resolvedPaymentAmount(firstPresent(offer.trip_cost, offer.amount, selectedBid.trip_cost, selectedBid.amount), "acceptedDriverOffer", true);
  }
  if (booking.acceptedFleetOffer) {
    const offer = booking.acceptedFleetOffer || {};
    return resolvedPaymentAmount(firstPresent(offer.amount, offer.bidAmount), "acceptedFleetOffer", true);
  }
  if (booking.selectedBid) {
    const selectedBidAmount = firstPresent(booking.selectedBid.trip_cost, booking.selectedBid.amount);
    if (selectedBidAmount !== undefined) {
      return resolvedPaymentAmount(selectedBidAmount, "acceptedDriverOffer", true);
    }
  }
  const status = (booking.status || "").toString().toUpperCase();
  const hasAssignedDriverBid = booking.deliveryWithBid === true && !!booking.driver && ["ACCEPTED", "ARRIVED", "STARTED", "REACHED", "PENDING", "PAID", "COMPLETE", "COMPLETED"].indexOf(status) !== -1;
  if (hasAssignedDriverBid) {
    return resolvedPaymentAmount(booking.trip_cost, "acceptedDriverOffer", true);
  }
  if (booking.deliveryWithBid === true) return pendingPaymentAmount();
  return resolvedPaymentAmount(booking.trip_cost, "legacyTripCost", false);
};

const generateUniqueBookingReference = async (maxAttempts = 5) => {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const reference = generateBookingReference();
    try {
      const snapshot = await get(query(firebase.bookingRef, orderByChild("reference"), equalTo(reference)));
      if (!snapshot.exists()) {
        return reference;
      }
    } catch (error) {
      return reference;
    }
  }
  return generateBookingReference();
};

export const formatBookingObject = async (bookingData, settings) => {
  const reference = await generateUniqueBookingReference();
  const instructionData = { ...(bookingData.instructionData || {}) };
  const bookingContainerSize = getContainerSizeFromCarType(bookingData.carDetails);
  if (bookingContainerSize === "10ft") {
      [
        "containerNumber",
        "additionalContainerNumbers",
        "bookingNumber",
        "blNumber",
        "shippingLine",
        "containerType",
        "containerSize",
        "pickupPort",
        "deliveryWarehouse",
        "pickupContainerYardId",
        "pickupContainerYardName",
        "pickupContainerYardSlots",
        "emptyReturnContainerYardId",
        "emptyReturnContainerYardIds",
        "emptyReturnContainerYardName",
        "emptyReturnContainerYardSlots",
        "yardGateStatus",
        "yardGateRejectionReason",
        "freeTimeExpiry",
        "detentionDeadline",
        "demurrageDeadline",
        "dateConfirmationOk",
        "importBookingInfo",
        "exportBookingInfo"
      ].forEach((field) => {
        instructionData[field] = Array.isArray(instructionData[field]) ? [] : "";
      });
      instructionData.additionalContainerNumbers = [];
      instructionData.emptyReturnContainerYardIds = [];
      instructionData.dateConfirmationOk = false;
      instructionData.shipmentType = "Domestic Shipment";
  }

  const { config } = firebase;
  let today;
  try{
      let res =  await fetch(`https://${config.projectId}.web.app/getservertime`, { method: 'GET', headers: {'Content-Type': 'application/json'}});
      const json = await res.json();
      if(json.time){
        today = json.time;
      } else{
        today = new Date().getTime();
      }
  }catch (err){
    today = new Date().getTime();
  }

  let pickUp = { lat: bookingData.pickup.coords.lat, lng: bookingData.pickup.coords.lng, add: bookingData.pickup.description };
  let drop = { lat: bookingData.drop.coords.lat, lng: bookingData.drop.coords.lng, add: bookingData.drop.description };
  const bookingServiceType = (instructionData.serviceType || "").toString().trim().toLowerCase().replace(/[^a-z]+/g, "_").replace(/^_+|_+$/g, "");
  const isPickupDeliveryBooking = bookingServiceType === "pickup_delivery" || bookingServiceType === "pickupdelivery";
  const bookingWaypoints = isPickupDeliveryBooking && bookingData.drop.waypoints ? bookingData.drop.waypoints : null;
  
  let coords = [{latitude: pickUp.lat,longitude: pickUp.lng}];
  if(isPickupDeliveryBooking && bookingData.drop.waypointsStr){
      bookingData.drop.waypointsStr.split("|").forEach((point)=>{
          let lat = point.split(',')[0];
          let lng = point.split(',')[1];
          coords.push({latitude: parseFloat(lat), longitude: parseFloat(lng)});
      });
  }
  coords.push({latitude: drop.lat,longitude: drop.lng});
  
  var otp;
  if(bookingData.settings.otp_secure)
      otp = Math.floor(Math.random() * 90000) + 10000;
  else{
      otp = false;
  }

  const hasPrivateAdditionalFees = Array.isArray(bookingData.additionalFees);
  const privateAdditionalFeesTotal = bookingData.totalAdditionalFeeAmount !== undefined && bookingData.totalAdditionalFeeAmount !== null
      ? bookingData.totalAdditionalFeeAmount
      : null;
  const bookingAdditionalFees = hasPrivateAdditionalFees
      ? bookingData.additionalFees
      : (bookingData.estimate.additionalFees ? bookingData.estimate.additionalFees : null);
  const bookingAdditionalFeesTotal = privateAdditionalFeesTotal !== null
      ? privateAdditionalFeesTotal
      : (bookingData.estimate.additionalFeesTotal ? bookingData.estimate.additionalFeesTotal : 0);
  
  return {
      carType: bookingData.carDetails.name,
      carImage: bookingData.carDetails.image,
      customer: bookingData.userDetails.uid,
      customerUid: bookingData.clientId || bookingData.userDetails.uid,
      clientId: bookingData.clientId || null,
      clientAuthUid: bookingData.clientAuthUid ? bookingData.clientAuthUid : (bookingData.loginStatus === 'NO_ACCOUNT' ? null : bookingData.userDetails.uid),
      globalClientNumber: bookingData.globalClientNumber ? bookingData.globalClientNumber : null,
      organizationId: bookingData.organizationId ? bookingData.organizationId : (bookingData.fleetadmin ? bookingData.fleetadmin : null),
      organizationClientRelationshipId: bookingData.organizationClientRelationshipId ? bookingData.organizationClientRelationshipId : (bookingData.clientId ? bookingData.clientId : null),
      organizationClientNumber: bookingData.organizationClientNumber ? bookingData.organizationClientNumber : null,
      commission_type: bookingData.carDetails.convenience_fee_type,
      commission_rate: bookingData.carDetails.convenience_fees,
      reference: reference,
      customer_email: bookingData.userDetails.email,
      customer_name: bookingData.userDetails.firstName + ' ' + bookingData.userDetails.lastName,
      customer_contact: bookingData.userDetails.mobile? bookingData.userDetails.mobile: ' ',
      customer_token: bookingData.userDetails.pushToken? bookingData.userDetails.pushToken: ' ',
      customer_image: bookingData.userDetails.profile_image ? bookingData.userDetails.profile_image : "",
      drop: drop,
      pickup: pickUp,
      estimate: bookingData.estimate.estimateFare,
      baseEstimateFare: bookingData.estimate.baseEstimateFare ? bookingData.estimate.baseEstimateFare : bookingData.estimate.estimateFare,
      additionalFees: bookingAdditionalFees,
      additionalFeesTotal: bookingAdditionalFeesTotal,
      totalAdditionalFeeAmount: bookingData.totalAdditionalFeeAmount !== undefined ? bookingData.totalAdditionalFeeAmount : null,
      estimateDistance: bookingData.estimate.estimateDistance,
      distance: bookingData.estimate.estimateDistance,
      estimateTime:bookingData.estimate.estimateTime,
      status: bookingData.booking_type_admin || bookingData.deliveryWithBid || bookingData.booking_type_fleetadmin ? "NEW" : "PAYMENT_PENDING",
      bookLater:bookingData.bookLater,
      tripdate: bookingData.bookLater?bookingData.tripdate:today,
      bookingDate: today,
      otp: otp,
      booking_type_admin:bookingData.booking_type_admin,
      coords: coords,
      waypoints: bookingWaypoints,
      roundTrip: bookingData.roundTrip? bookingData.roundTrip:null,
      tripInstructions: bookingData.tripInstructions? bookingData.tripInstructions: null,
      trip_cost: bookingData.estimate.estimateFare,
      convenience_fees: bookingData.estimate.convenience_fees,
      driver_share: (parseFloat(bookingData.estimate.estimateFare) - parseFloat(bookingData.estimate.convenience_fees)).toFixed(settings.decimal),
      paymentPacket: bookingData.paymentPacket? bookingData.paymentPacket : null,
      preRequestedDrivers: bookingData.preRequestedDrivers?  bookingData.preRequestedDrivers: null,
      requestedDrivers: bookingData.requestedDrivers?  bookingData.requestedDrivers: null,
      driverEstimates: bookingData.driverEstimates?  bookingData.driverEstimates: null,
      ...instructionData,
      fleetadmin: bookingData.fleetadmin? bookingData.fleetadmin:null,
      bookingSource: bookingData.bookingSource === 'fleet_admin_client' ? 'LOGISTICS_COMPANY' : (bookingData.booking_type_fleetadmin ? 'LOGISTICS_COMPANY' : (bookingData.booking_type_admin ? 'SUPER_ADMIN' : 'CLIENT')),
      bookingChannel: bookingData.bookingChannel ? bookingData.bookingChannel : (bookingData.deliveryWithBid ? 'OPEN_MARKETPLACE' : 'DIRECT_LOGISTICS'),
      privateBooking: bookingData.deliveryWithBid ? false : true,
      createdByRole: bookingData.createdByRole ? bookingData.createdByRole : null,
      createdBy: bookingData.createdBy ? bookingData.createdBy : null,
      visibility: bookingData.visibility ? bookingData.visibility : null,
      clientName: bookingData.clientName ? bookingData.clientName : null,
      clientContact: bookingData.clientContact ? bookingData.clientContact : null,
      clientEmail: bookingData.clientEmail ? bookingData.clientEmail : null,
      clientAddress: bookingData.clientAddress ? bookingData.clientAddress : null,
      truckingChargeToClient: bookingData.truckingChargeToClient !== undefined ? bookingData.truckingChargeToClient : null,
      superAdminCommissionType: bookingData.superAdminCommissionType ? bookingData.superAdminCommissionType : null,
      superAdminCommissionValue: bookingData.superAdminCommissionValue !== undefined ? bookingData.superAdminCommissionValue : null,
      superAdminCommissionAmount: bookingData.superAdminCommissionAmount !== undefined ? bookingData.superAdminCommissionAmount : null,
      fleetAdminNetAmount: bookingData.fleetAdminNetAmount !== undefined ? bookingData.fleetAdminNetAmount : null,
      fleetAdminWalletThresholdType: bookingData.fleetAdminWalletThresholdType || null,
      fleetAdminWalletThresholdValue: bookingData.fleetAdminWalletThresholdValue !== undefined ? bookingData.fleetAdminWalletThresholdValue : null,
      fleetAdminWalletMinimumFlatRate: bookingData.fleetAdminWalletMinimumFlatRate !== undefined ? bookingData.fleetAdminWalletMinimumFlatRate : null,
      walletBalanceAtBooking: bookingData.walletBalanceAtBooking !== undefined ? bookingData.walletBalanceAtBooking : null,
      walletThresholdRequiredAmount: bookingData.walletThresholdRequiredAmount !== undefined ? bookingData.walletThresholdRequiredAmount : null,
      walletThresholdPassed: bookingData.walletThresholdPassed !== undefined ? bookingData.walletThresholdPassed === true : null,
      payment_mode: bookingData.payment_mode,
      fleet_admin_comission: bookingData.carDetails.fleet_admin_fee ? bookingData.carDetails.fleet_admin_fee : null,
      booking_from_web: bookingData.booking_from_web? bookingData.booking_from_web: false,
      deliveryWithBid: bookingData.deliveryWithBid ? bookingData.deliveryWithBid : false,
      booking_type_fleetadmin:bookingData.booking_type_fleetadmin,
  }
}

export const saveAddresses = async (booking, driverLocation) => {
  const { singleUserRef } = firebase;
  let address = booking.drop.add;
  
  onValue(child(singleUserRef(booking.customer), "savedAddresses"), (savedAdd) => {
      if (savedAdd.val()) {
        let addresses = savedAdd.val();
        let didNotMatch = true;
        for (let key in addresses) {
          let entry = addresses[key];
          if (
            GetDistance(
              entry.lat,
              entry.lng,
              booking.drop.lat,
              booking.drop.lng
            ) < 0.1
          ) {
            didNotMatch = false;
            let count = entry.count ? entry.count + 1 : 1;
            update(child(singleUserRef(booking.customer),"savedAddresses/" + key),{ count: count });
            break;
          }
        }
        if (didNotMatch) {
          push(child(singleUserRef(booking.customer),"savedAddresses"),{
            description: address,
            lat: booking.drop.lat,
            lng: booking.drop.lng,
            count: 1,
          });
        }
      } else {
        push(child(singleUserRef(booking.customer),"savedAddresses"),{
          description: address,
          lat: booking.drop.lat,
          lng: booking.drop.lng,
          count: 1,
        });
      }
    }, {onlyOnce: true});
  return address;
};

export const addActualsToBooking = async (booking, address, driverLocation) => {
  const end_time = new Date();
  const diff = (end_time.getTime() - parseFloat(booking.startTime)) / 1000;
  const totalTimeTaken = Math.abs(Math.round(diff));
  booking.trip_end_time = end_time.getHours() + ":" + end_time.getMinutes() + ":" + end_time.getSeconds();
  booking.endTime = end_time.getTime();
  booking.total_trip_time = totalTimeTaken;
  return booking;
};

export const updateDriverQueue = (booking) => {
  return booking;
};

export const driverQueue= true;
